import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type ChecklistStep,
  type DateMention,
  type DirectiveId,
  distinctFormatting,
  distinctStyles,
  type FormatId,
  type LengthRequirement,
  type LengthUnit,
  type PromptAnalysis,
  type SourceKind,
} from "@/lib/tool-kit/essay/prompt-analyzer";

/**
 * Words for the Essay Prompt Analyzer's findings.
 *
 * Pure functions over the widget's (possibly translated) strings, so the sentence a
 * reader sees for a length limit or a plan step is decided in one tested place rather
 * than inline in JSX.
 */

export interface PromptTextStrings {
  lengthRange: string;
  lengthMin: string;
  lengthMax: string;
  lengthAbout: string;
  unitWords: string;
  unitPages: string;
  unitParagraphs: string;
  unitCharacters: string;
  unitMinutes: string;
  steps: Readonly<
    Record<
      | "read"
      | "directive"
      | "terms"
      | "sources"
      | "style"
      | "length"
      | "format"
      | "deadlineDraft"
      | "deadlineFinal"
      | "deadline"
      | "thesis"
      | "final",
      string
    >
  >;
  directiveLabels: Readonly<Record<DirectiveId, string>>;
  directiveTasks: Readonly<Record<DirectiveId, string>>;
  groups: Readonly<
    Record<"length" | "sources" | "style" | "dates" | "format", string>
  >;
  sourceKinds: Readonly<Record<SourceKind, string>>;
  dateKinds: Readonly<Record<"draft" | "final" | "deadline" | "date", string>>;
  formatLabels: Readonly<Record<FormatId, string>>;
  quoted: string;
  styleEdition: string;
  styleGuideLink: string;
  dateWithTime: string;
}

type Format = (value: number) => string;

/**
 * The unit's word in the right plural form for `count`, with its leading space — the
 * repo's plural messages carry the space after the colon ("one: word|other: words"),
 * so templates write "{n}{unit}" rather than "{n} {unit}".
 */
export function unitLabel(
  strings: PromptTextStrings,
  unit: LengthUnit,
  locale: string,
  count: number
): string {
  const messages: Record<LengthUnit, string> = {
    words: strings.unitWords,
    pages: strings.unitPages,
    paragraphs: strings.unitParagraphs,
    characters: strings.unitCharacters,
    minutes: strings.unitMinutes,
  };
  return tPlural(messages[unit], locale, count);
}

/** "1,500–2,000 words", "at least 5 pages", "no more than 1,200 words", "about 2,500 words". */
export function lengthText(
  item: LengthRequirement,
  strings: PromptTextStrings,
  locale: string,
  fmt: Format
): string {
  const unit = (n: number) => unitLabel(strings, item.unit, locale, n);
  if (item.min !== null && item.max !== null && item.min !== item.max) {
    return t(strings.lengthRange, {
      min: fmt(item.min),
      max: fmt(item.max),
      unit: unit(item.max),
    });
  }
  if (item.min !== null && item.max === null) {
    return t(strings.lengthMin, { min: fmt(item.min), unit: unit(item.min) });
  }
  if (item.min === null && item.max !== null) {
    return t(strings.lengthMax, { max: fmt(item.max), unit: unit(item.max) });
  }
  const n = item.min ?? 0;
  return t(strings.lengthAbout, { n: fmt(n), unit: unit(n) });
}

export type DateKind = "draft" | "final" | "deadline" | "date";

export function dateKind(date: DateMention): DateKind {
  if (!date.deadline) {
    return "date";
  }
  return date.stage ?? "deadline";
}

/** One plan step as the sentence the reader ticks off. */
export function stepText(
  step: ChecklistStep,
  strings: PromptTextStrings
): string {
  const detail = step.detail ?? "";
  switch (step.id) {
    case "directive": {
      const id = step.directive ?? "analyze";
      return t(strings.steps.directive, {
        label: strings.directiveLabels[id],
        task: strings.directiveTasks[id],
      });
    }
    case "deadline": {
      if (step.stage === "draft") {
        return t(strings.steps.deadlineDraft, { detail });
      }
      if (step.stage === "final") {
        return t(strings.steps.deadlineFinal, { detail });
      }
      return t(strings.steps.deadline, { detail });
    }
    default:
      return t(strings.steps[step.id], { detail });
  }
}

/** One line in a requirements group. */
export interface Finding {
  key: string | number;
  label?: string;
  text: string;
  /** The prompt's own words, shown after the reading. */
  quote?: string;
  link?: { href: string; text: string };
}

export interface FindingSet {
  title: string;
  items: Finding[];
  /** Spans both columns on wide screens. */
  wide?: boolean;
}

/** The requirements panel: length, sources, style, dates, formatting — in that order. */
export function requirementSets(
  analysis: PromptAnalysis,
  strings: PromptTextStrings,
  locale: string,
  fmt: Format
): FindingSet[] {
  const quote = (text: string) => t(strings.quoted, { text });
  return [
    {
      title: strings.groups.length,
      items: analysis.lengths.map((item) => ({
        key: item.start,
        text: lengthText(item, strings, locale, fmt),
        quote: item.text,
      })),
    },
    {
      title: strings.groups.sources,
      items: analysis.sources.map((item) => ({
        key: item.start,
        label: strings.sourceKinds[item.kind],
        text: quote(item.text),
      })),
    },
    {
      title: strings.groups.style,
      items: distinctStyles(analysis.styles).map((style) => ({
        key: style.id,
        text:
          style.edition === null
            ? style.name
            : t(strings.styleEdition, {
                name: style.name,
                edition: fmt(style.edition),
              }),
        link: {
          href: style.href,
          text: t(strings.styleGuideLink, { name: style.name }),
        },
      })),
    },
    {
      title: strings.groups.dates,
      items: analysis.dates.map((date) => ({
        key: date.start,
        label: strings.dateKinds[dateKind(date)],
        text: date.time
          ? t(strings.dateWithTime, { date: date.text, time: date.time })
          : date.text,
      })),
    },
    {
      title: strings.groups.format,
      wide: true,
      items: distinctFormatting(analysis.formatting).map((item) => ({
        key: item.id,
        label: strings.formatLabels[item.id],
        text: quote(item.text),
      })),
    },
  ];
}
